import type { Lang } from '../domain/types';

/**
 * Offline city search. `cities.json` is generated by `scripts/build-world-data.mjs`
 * (GeoNames, CC-BY) and loaded lazily the first time someone searches, so the
 * rest of the app never pays for it. A row is
 * `[name, countryCode, lat, lng, timezoneIndex, population, hebrewName?]`.
 */
export interface CityHit {
  /** Stable key: `${countryCode}:${name}:${lat},${lng}` — stored on the Location as `cityRef`. */
  ref: string;
  name: string;
  nameHe?: string;
  countryCode: string;
  country: string;
  countryHe: string;
  lat: number;
  lng: number;
  timezone: string;
  population: number;
}

interface CityFile {
  countries: Record<string, string>;
  timezones: string[];
  cities: Array<[string, string, number, number, number, number] | [string, string, number, number, number, number, string]>;
}

let loaded: Promise<CityHit[]> | null = null;

function countryNameHe(code: string, fallback: string): string {
  try {
    const dn = new Intl.DisplayNames(['he'], { type: 'region' });
    return dn.of(code) ?? fallback;
  } catch {
    return fallback;
  }
}

export function cityRef(countryCode: string, name: string, lat: number, lng: number): string {
  return `${countryCode}:${name}:${lat},${lng}`;
}

export function loadCities(): Promise<CityHit[]> {
  if (!loaded) {
    loaded = import('./cities.json').then((m) => {
      const file = (m.default ?? m) as unknown as CityFile;
      const heCache = new Map<string, string>();
      return file.cities.map((row) => {
        const [name, cc, lat, lng, tz, pop, he] = row;
        const country = file.countries[cc] ?? cc;
        let countryHe = heCache.get(cc);
        if (!countryHe) {
          countryHe = countryNameHe(cc, country);
          heCache.set(cc, countryHe);
        }
        return { ref: cityRef(cc, name, lat, lng), name, nameHe: he || undefined, countryCode: cc, country, countryHe, lat, lng, timezone: file.timezones[tz] ?? 'UTC', population: pop };
      });
    });
  }
  return loaded;
}

/** Lower-case, accent-stripped, for matching what people type. */
export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[ְ-ׇ]/g, '') // Hebrew points
    .replace(/[’'`"־\-–—.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Rank cities for a query: exact name first, then prefix, then word-prefix,
 * then substring; ties broken by population. Both scripts are searched, so
 * "חיפה" and "Haifa" find the same row. Country names match too ("Haifa Israel").
 */
export function rankCities(cities: CityHit[], query: string, lang: Lang, limit = 8): CityHit[] {
  const q = normalizeText(query);
  if (!q) return [];
  const words = q.split(' ');
  const first = words[0];
  const scored: Array<{ c: CityHit; s: number }> = [];
  for (const c of cities) {
    const names = [normalizeText(c.name)];
    if (c.nameHe) names.push(normalizeText(c.nameHe));
    let best = 0;
    for (const n of names) {
      let s = 0;
      if (n === q) s = 100;
      else if (n.startsWith(q)) s = 80;
      else if (n.startsWith(first)) s = 60;
      else if (n.split(' ').some((w) => w.startsWith(first))) s = 50;
      else if (n.includes(first)) s = 30;
      if (s && words.length > 1) {
        const rest = words.slice(1).join(' ');
        const countries = [normalizeText(c.country), normalizeText(c.countryHe), c.countryCode.toLowerCase()];
        if (n.includes(rest) || countries.some((x) => x.startsWith(rest))) s += 15;
        else if (!n.startsWith(q)) s -= 25;
      }
      if (s > best) best = s;
    }
    if (best > 0) scored.push({ c, s: best + Math.min(9, Math.log10(Math.max(1, c.population))) });
  }
  scored.sort((a, b) => b.s - a.s || b.c.population - a.c.population);
  void lang;
  return scored.slice(0, limit).map((x) => x.c);
}

export function cityLabel(c: { name: string; nameHe?: string; country: string; countryHe?: string }, lang: Lang): string {
  return lang === 'he' ? `${c.nameHe || c.name}, ${c.countryHe || c.country}` : `${c.name}, ${c.country}`;
}
